import React from 'react';
import {
  ArrowsClockwise, ArrowLeft, MapPin, Phone, User, Package, WarningCircle,
  CheckCircle,
} from '@phosphor-icons/react';

const CARD = 'rounded-card border border-paper-border bg-paper-panel shadow-soft';
const LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-ink-faint';
const VALUE = 'text-sm text-ink';

function skuSummary(t) {
  const parts = [];
  if (t.product_sku) parts.push(t.product_sku);
  if (t.product_quantity != null) {
    parts.push(t.product_quantity + (t.product_quantity_unit ? ' ' + t.product_quantity_unit : ''));
  }
  return parts.length ? parts.join(' · ') : null;
}

// Address block — handles all four address_status values.
// The verify-with-customer note appears only when the returned address is a
// most-recent-order fallback (needs_verify=true). No address = graceful
// "call the customer" fallback.
function AddressBlock({ task }) {
  const { pickup_address, address_needs_verify, address_status, customer_mobile } = task;
  if (address_status === 'ok' && pickup_address) {
    return (
      <div>
        <div className={LABEL}>Pickup Address {address_needs_verify && <span className="ml-1 text-status-pending-finance">⚠ verify</span>}</div>
        <div className="mt-1.5 whitespace-pre-line rounded-field border border-paper-border2 bg-paper p-3 text-sm text-ink" dir="auto">{pickup_address}</div>
        {address_needs_verify && (
          <div className="mt-1 text-[11px] text-status-pending-finance">
            Most recent order address — verify with customer before pickup.
          </div>
        )}
      </div>
    );
  }
  const fallback = address_status === 'no_mobile'
    ? 'No mobile number on file — no HubSpot lookup possible.'
    : address_status === 'no_contact'
      ? 'No HubSpot contact matches this mobile.'
      : address_status === 'no_deal'
        ? 'HubSpot contact has no delivery-address deal.'
        : address_status === 'hs_error'
          ? 'HubSpot lookup failed — try again later.'
          : 'Address unavailable.';
  return (
    <div>
      <div className={LABEL}>Pickup Address</div>
      <div className="mt-1.5 rounded-field border border-status-pending-finance/30 bg-status-pending-finance/8 p-3 text-sm text-status-pending-finance">
        <div className="flex items-start gap-2">
          <WarningCircle size={16} weight="fill" className="mt-0.5 flex-none" />
          <div>
            <div className="font-medium">Address unavailable</div>
            <div className="mt-0.5 text-[12px] opacity-90">{fallback}</div>
            {customer_mobile && (
              <div className="mt-2 text-[13px] text-ink">
                <Phone size={14} className="mr-1 inline align-[-2px]" />
                Call the customer at <b>{customer_mobile}</b> to coordinate.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   task: object|null, loading: boolean, advancing: boolean,
 *   onAdvance: (stage:string)=>void, onBack: ()=>void,
 * }} props
 */
export default function SampleCollectionDetailView({ task, loading, advancing, onAdvance, onBack }) {
  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center text-ink-faint">
        <ArrowsClockwise size={26} className="animate-spin" />
      </div>
    );
  }
  if (!task) {
    return (
      <div className="p-1">
        <div className={`${CARD} p-8 text-center`}>
          <p className="font-serif text-lg text-ink">Collection task not found</p>
          <p className="mt-1 text-[13px] text-ink-muted">The sample may have moved past the collection stage or been resolved.</p>
          <button onClick={onBack} className="mt-4 inline-flex items-center gap-1.5 rounded-field border border-paper-border2 px-3 py-1.5 text-[13px] text-ink hover:bg-paper-muted">
            <ArrowLeft size={14} /> Back to Sample Collections
          </button>
        </div>
      </div>
    );
  }

  const stage = task.sample_stage || null;
  const canMarkCollected = stage === null || stage === 'none';
  const canMarkWarehouse = stage === 'logistics';
  const sku = skuSummary(task);

  return (
    <div className="p-1">
      <div className={`${CARD}`}>

        {/* Header */}
        <div className="border-b border-paper-border p-6">
          <button onClick={onBack} className="mb-3 inline-flex items-center gap-1.5 text-[12px] text-ink-faint hover:text-ink-muted">
            <ArrowLeft size={12} /> Sample Collections
          </button>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-serif text-2xl text-ink truncate">{task.ticket_number || 'Collection Task'}</h1>
                {stage === 'logistics' && (
                  <span className="inline-flex items-center gap-1 rounded-chip bg-status-pending-finance/15 px-2 py-0.5 text-[11px] font-medium text-status-pending-finance">
                    <CheckCircle size={11} weight="fill" /> With Logistics
                  </span>
                )}
              </div>
              <p className="mt-1 text-[13px] text-ink-muted">Physical sample pickup for a Quality-Complaint investigation.</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 gap-5 p-6 lg:grid-cols-2">
          {/* Left column: customer + address */}
          <div className="space-y-5">
            <div>
              <div className={LABEL}>Customer</div>
              <div className="mt-1.5 flex items-center gap-2">
                <User size={16} className="text-ink-faint" />
                <span className={VALUE} dir="auto">{task.customer_name || <span className="italic text-ink-faint">Not recorded</span>}</span>
              </div>
              {task.customer_mobile && (
                <div className="mt-1 flex items-center gap-2">
                  <Phone size={16} className="text-ink-faint" />
                  <a href={`tel:${task.customer_mobile}`} className={`${VALUE} hover:underline`}>{task.customer_mobile}</a>
                </div>
              )}
              {task.customer_area && (
                <div className="mt-1 flex items-center gap-2">
                  <MapPin size={16} className="text-ink-faint" />
                  <span className={VALUE} dir="auto">{task.customer_area}</span>
                </div>
              )}
            </div>

            <AddressBlock task={task} />
          </div>

          {/* Right column: product + actions */}
          <div className="space-y-5">
            <div>
              <div className={LABEL}>Product</div>
              <div className="mt-1.5 flex items-start gap-2">
                <Package size={16} className="mt-0.5 text-ink-faint" />
                <div className="min-w-0">
                  {sku ? (
                    <div className={VALUE}>{sku}</div>
                  ) : (
                    <div className="text-sm italic text-ink-faint">SKU not recorded on this ticket</div>
                  )}
                  {task.product_sku_brand && (
                    <div className="mt-0.5 text-[12px] text-ink-muted">Brand: {task.product_sku_brand}</div>
                  )}
                </div>
              </div>
            </div>

            {task.sap_order_reference && (
              <div>
                <div className={LABEL}>Order Reference</div>
                <div className={`mt-1.5 ${VALUE}`}>{task.sap_order_reference}</div>
              </div>
            )}

            <div>
              <div className={LABEL}>Actions</div>
              <div className="mt-2 flex flex-col gap-2">
                {canMarkCollected && (
                  <button
                    onClick={() => onAdvance('logistics')}
                    disabled={advancing}
                    className="inline-flex items-center justify-center gap-1.5 rounded-field bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
                    Mark Sample Collected
                  </button>
                )}
                {canMarkWarehouse && (
                  <button
                    onClick={() => onAdvance('warehouse')}
                    disabled={advancing}
                    className="inline-flex items-center justify-center gap-1.5 rounded-field bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
                    Mark Handed to Warehouse
                  </button>
                )}
                {task.sample_logistics_at && (
                  <div className="text-[11px] text-ink-faint">Collected {new Date(task.sample_logistics_at).toLocaleString()}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
